import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BoldDiagonalRed from "@/components/feature/BoldDiagonalRed";

export default function CtaSection() {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setRevealed(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px -50px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      className="relative py-16 md:py-28 px-4 md:px-6"
      style={{ background: "linear-gradient(to right, #e8ddd8, #ffffff)", borderTop: "1px solid #e0e0e0" }}
    >
      {/* Bold diagonal red lines — subtle corner */}
      <BoldDiagonalRed pattern="corner" zIndex={0} opacity={0.06} />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto">
        <div className="text-center mb-14 md:mb-20">
          {/* Accent line top */}
          <div
            className="mx-auto"
            style={{
              width: "32px",
              height: "2px",
              background: "#c00000",
              transform: revealed ? "scaleX(1)" : "scaleX(0)",
              transformOrigin: "center",
              transition: "transform 0.8s cubic-bezier(0.22,1,0.36,1)",
              marginBottom: "16px",
            }}
          />

          <span
            className="text-sm tracking-[0.4em] font-medium block"
            style={{
              fontFamily: "'Barlow', sans-serif",
              color: "#c00000",
              opacity: revealed ? 1 : 0,
              transform: revealed ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.6s ease 0.15s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.15s",
            }}
          >
            CONTACT
          </span>

          <h2
            className="text-2xl md:text-3xl font-bold mt-3"
            style={{
              fontFamily: "'Barlow', sans-serif",
              color: "#111111",
              opacity: revealed ? 1 : 0,
              transform: revealed ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 0.7s ease 0.25s, transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.25s",
            }}
          >
            お問い合わせ
          </h2>

          <div
            className="mx-auto mt-4"
            style={{
              width: "32px",
              height: "2px",
              background: "#c00000",
              transform: revealed ? "scaleX(1)" : "scaleX(0)",
              transformOrigin: "center",
              transition: "transform 0.8s cubic-bezier(0.22,1,0.36,1) 0.3s",
            }}
          />

          <p
            className="text-sm md:text-base leading-loose mt-8 mx-auto"
            style={{
              fontFamily: "'Noto Sans JP', sans-serif",
              color: "#111111",
              opacity: revealed ? 1 : 0,
              transform: revealed ? "translateY(0)" : "translateY(12px)",
              transition: "opacity 0.6s ease 0.4s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.4s",
            }}
          >
            ご不明点などございましたらお気軽にお問い合わせください。
          </p>
        </div>

        <div
          className="grid grid-cols-2 gap-3 md:gap-6"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? "translateY(0) scale(1)" : "translateY(20px) scale(0.98)",
            transition: "opacity 0.8s ease 0.5s, transform 0.8s cubic-bezier(0.22,1,0.36,1) 0.5s",
          }}
        >
          <div
            className="flex flex-col items-center text-center p-4 md:p-8 cursor-pointer"
            style={{
              background: "#ffffff",
              border: "1px solid #e0e0e0",
              transition: "border-color 0.3s, transform 0.3s",
            }}
            onClick={() => navigate("/contact")}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#c00000";
              e.currentTarget.style.transform = "translateY(-4px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e0e0e0";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <div
              className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-4"
              style={{ background: "#f0f0f0" }}
            >
              <i
                className="ri-mail-line text-base md:text-xl"
                style={{ color: "#111111" }}
              />
            </div>
            <h3
              className="font-bold text-sm md:text-base mb-1 md:mb-2"
              style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}
            >
              MAIL
            </h3>
            <span
              className="text-xs md:text-sm font-medium flex items-center gap-1"
              style={{ fontFamily: "'Barlow', sans-serif", color: "#c00000" }}
            >
              <span className="hidden md:inline">フォームを開く</span>{" "}
              <i className="ri-arrow-right-line" />
            </span>
          </div>

          <a
            href="https://lin.ee/tP9rADP"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center text-center p-4 md:p-8"
            style={{
              background: "#ffffff",
              border: "1px solid #e0e0e0",
              transition: "border-color 0.3s, transform 0.3s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#06C755";
              e.currentTarget.style.transform = "translateY(-4px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e0e0e0";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <div
              className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-4"
              style={{ background: "#06C755" }}
            >
              <i
                className="ri-line-fill text-base md:text-xl"
                style={{ color: "#ffffff" }}
              />
            </div>
            <h3
              className="font-bold text-sm md:text-base mb-1 md:mb-2"
              style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}
            >
              LINE
            </h3>
            <span
              className="text-xs md:text-sm font-medium flex items-center gap-1"
              style={{ fontFamily: "'Barlow', sans-serif", color: "#06C755" }}
            >
              <span className="hidden md:inline">LINEを開く</span>{" "}
              <i className="ri-external-link-line" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}